import { FORTA_BLACK, FORTA_BLUE, FORTA_ERROR, FORTA_WHITE } from '../../../utils/colors';
import {
  DATE_TIME_PICKER_INPUT_HEIGHT,
  DATE_TIME_PICKER_VALUE_FONT_SIZE,
} from '../fDateTimePicker/constants';

import type { SxProps, Theme } from '@mui/material/styles';
import type { TimePickerPlaceholderStyleState, TimePickerStyleState } from './types';

export function buildTimePickerTextFieldSx(state: TimePickerStyleState): SxProps<Theme> {
  const borderColor = state.error ? FORTA_ERROR : state.outlineColor ?? FORTA_BLUE.light1;

  return {
    width: state.fullWidth ? '100%' : undefined,
    '& .MuiInputBase-root': {
      border: 0,
      borderRadius: '8px',
      boxSizing: 'border-box',
      backgroundColor: FORTA_WHITE,
      color: FORTA_BLACK,
      cursor: state.disabled ? 'not-allowed' : 'pointer',
      fontSize: DATE_TIME_PICKER_VALUE_FONT_SIZE,
      height: DATE_TIME_PICKER_INPUT_HEIGHT,
      opacity: state.disabled ? 0.38 : 1,
      transition: 'border-color 150ms',
    },
    '& .MuiOutlinedInput-root': {
      border: 0,
      borderRadius: '8px',
    },
    '& .MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
      paddingRight: 0,
    },
    '& .MuiOutlinedInput-root:hover': {
      border: 0,
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      border: 0,
    },
    '& .MuiInputBase-input': {
      height: DATE_TIME_PICKER_INPUT_HEIGHT,
      boxSizing: 'border-box',
      color: state.showPreviewPlaceholder ? 'transparent' : FORTA_BLACK,
      cursor: state.disabled ? 'not-allowed' : 'pointer',
      caretColor: state.showPreviewPlaceholder ? 'transparent' : FORTA_BLACK,
      padding: '0 14px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${borderColor} !important`,
      borderRadius: '8px',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${borderColor} !important`,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${borderColor} !important`,
    },
    '& .MuiInputAdornment-root': {
      marginLeft: 0,
      marginRight: 0,
    },
    '& .MuiIconButton-root': {
      color: FORTA_BLUE.primary,
      cursor: state.disabled ? 'not-allowed' : 'pointer',
      height: DATE_TIME_PICKER_INPUT_HEIGHT,
      margin: 0,
      padding: 0,
      width: DATE_TIME_PICKER_INPUT_HEIGHT,
    },
    '& .MuiSvgIcon-root': {
      height: 20,
      width: 20,
    },
  };
}

export function buildTimePickerPlaceholderSx(
  state: TimePickerPlaceholderStyleState,
): SxProps<Theme> {
  return {
    color: FORTA_BLACK,
    fontSize: DATE_TIME_PICKER_VALUE_FONT_SIZE,
    left: 14,
    lineHeight: `${DATE_TIME_PICKER_INPUT_HEIGHT}px`,
    opacity: state.disabled ? 0.38 : 0.6,
    overflow: 'hidden',
    pointerEvents: 'none',
    position: 'absolute',
    right: state.showClockIcon ? 44 : 14,
    textAlign: 'left',
    textOverflow: 'ellipsis',
    top: 0,
    whiteSpace: 'nowrap',
  };
}

export function buildTimePickerOptionSx(disabledMeridiem: Array<'am' | 'pm'>): SxProps<Theme> {
  return {
    '&[aria-label="AM"]': { display: disabledMeridiem.includes('am') ? 'none' : undefined },
    '&[aria-label="PM"]': { display: disabledMeridiem.includes('pm') ? 'none' : undefined },
  };
}

export const TIME_PICKER_POPPER_SX: SxProps<Theme> = {
  '& .MuiPaper-root': {
    border: `1px solid ${FORTA_BLUE.light2}`,
    borderRadius: 0,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.22)',
  },
  '& .MuiMultiSectionDigitalClock-root': {
    borderBottom: `1px solid ${FORTA_BLUE.light2}`,
    gap: 0.75,
    padding: 0.75,
    position: 'relative',
  },
  '& .MuiMultiSectionDigitalClockSection-root': {
    border: `1px solid ${FORTA_BLUE.light2}`,
    maxHeight: 226,
    overflowY: 'auto',
    scrollbarWidth: 'none',
    width: 54,
  },
  '& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar': {
    display: 'none',
  },
  '& .MuiMultiSectionDigitalClockSection-root:last-of-type, & .f-time-picker-meridiem-column': {
    alignSelf: 'flex-start',
    height: '226px !important',
    maxHeight: 226,
    overflow: 'hidden !important',
    overflowY: 'hidden !important',
    overscrollBehavior: 'none',
  },
  '& .MuiMultiSectionDigitalClockSection-root:last-of-type:after, & .f-time-picker-meridiem-column:after': {
    display: 'none',
  },
  '& .MuiMenuItem-root': {
    boxSizing: 'border-box',
    borderRadius: 10,
    color: FORTA_BLACK,
    fontSize: 14,
    justifyContent: 'center',
    margin: '0 auto',
    minHeight: 30,
    minWidth: 0,
    padding: 0,
    width: 38,
  },
  '& .f-time-picker-column-arrow': {
    alignItems: 'center',
    display: 'flex',
    height: 28,
    justifyContent: 'center',
    margin: 0,
    padding: 0,
    pointerEvents: 'none',
    position: 'absolute',
    zIndex: 2,
  },
  '& .f-time-picker-column-arrow-up': {
    top: 6,
  },
  '& .f-time-picker-column-arrow-down': {
    bottom: 6,
  },
  '& .f-time-picker-column-arrow-button': {
    alignItems: 'center',
    backgroundColor: FORTA_WHITE,
    border: `1px solid ${FORTA_BLUE.light2}`,
    borderRadius: 4,
    cursor: 'pointer',
    display: 'flex',
    height: 22,
    justifyContent: 'center',
    padding: 0,
    pointerEvents: 'auto',
    width: 34,
  },
  '& .f-time-picker-column-arrow-button:before': {
    borderLeft: '6px solid transparent',
    borderRight: '6px solid transparent',
    content: '""',
    display: 'block',
    height: 0,
    width: 0,
  },
  '& .f-time-picker-column-arrow-up .f-time-picker-column-arrow-button:before': {
    borderBottom: `8px solid ${FORTA_BLUE.light1}`,
  },
  '& .f-time-picker-column-arrow-down .f-time-picker-column-arrow-button:before': {
    borderTop: `8px solid ${FORTA_BLUE.light1}`,
  },
  '& .MuiMenuItem-root.Mui-selected': {
    backgroundColor: FORTA_BLUE.light1,
    color: FORTA_WHITE,
    fontWeight: 700,
  },
  '& .MuiMenuItem-root.Mui-selected:hover': {
    backgroundColor: FORTA_BLUE.light1,
  },
  '& .MuiPickersLayout-actionBar': {
    justifyContent: 'space-between',
    padding: '10px 8px 8px',
  },
  '& .MuiPickersLayout-actionBar .MuiButton-root': {
    border: `1px solid ${FORTA_BLUE.light2}`,
    borderRadius: 4,
    color: FORTA_BLACK,
    fontSize: 12,
    fontWeight: 700,
    minWidth: 70,
  },
  '& .MuiPickersLayout-actionBar .MuiButton-root:last-of-type': {
    backgroundColor: FORTA_BLUE.primary,
    borderColor: FORTA_BLUE.primary,
    color: FORTA_WHITE,
  },
  '& .MuiPickersLayout-actionBar .MuiButton-root.Mui-disabled': {
    borderColor: FORTA_ERROR,
  },
};
